import { ActionIcon, Badge, Card, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';

import { AttachmentRemoveButton, MessageAttachment } from '@/components/Chat/MessageAttachment';
import { useReportRequestAttachments } from '@/hooks/useReportRequestAttachments';
import { ConversationAttachment, ReportRequestAttachment } from '@/types/attachment';

interface ReportRequestAttachmentsProps {
  reportRequestId: string;
}

/** The per-file digest chip: async-submitted files digest in the
 * background — pending shows a spinner, failed shows why + offers a
 * retry; completed files need no chip. */
function DigestChip({
  attachment,
  onRetry,
}: Readonly<{ attachment: ReportRequestAttachment; onRetry: () => void }>) {
  if (attachment.digestStatus === 'pending') {
    return (
      <Badge
        size="xs"
        variant="light"
        color="blue"
        leftSection={<Loader size={8} color="blue" />}
        data-testid={`digest-pending-${attachment.filename}`}
      >
        Digesting…
      </Badge>
    );
  }
  if (attachment.digestStatus === 'failed') {
    return (
      <Group gap={4} wrap="nowrap">
        <Tooltip
          label={attachment.digestError ?? 'The file could not be read.'}
          multiline
          maw={280}
        >
          <Badge
            size="xs"
            variant="light"
            color="red"
            data-testid={`digest-failed-${attachment.filename}`}
          >
            Digest failed
          </Badge>
        </Tooltip>
        <Tooltip label="Retry digest">
          <ActionIcon
            size="xs"
            variant="subtle"
            color="red"
            onClick={onRetry}
            aria-label={`Retry digest for ${attachment.filename}`}
            data-testid={`digest-retry-${attachment.filename}`}
          >
            <IconRefresh size={12} />
          </ActionIcon>
        </Tooltip>
      </Group>
    );
  }
  return null;
}

/** Client files auto-flow (Backoffice dispatch + Project documents);
 * internal files are shared explicitly. The badge flips it. */
function ClassificationBadge({
  attachment,
  onFlip,
}: Readonly<{ attachment: ReportRequestAttachment; onFlip: () => void }>) {
  if (!attachment.classification) {
    return null;
  }
  const isClient = attachment.classification === 'client';
  return (
    <Tooltip
      label={
        isClient
          ? 'Client file — sent to Backoffice automatically. Click to make Internal.'
          : 'Internal file — only shared explicitly. Click to make Client.'
      }
      multiline
      maw={260}
    >
      <Badge
        size="xs"
        variant={isClient ? 'light' : 'outline'}
        color={isClient ? 'teal' : 'gray'}
        style={{ cursor: 'pointer' }}
        onClick={onFlip}
        data-testid={`classification-${attachment.filename}`}
      >
        {isClient ? 'Client' : 'Internal'}
      </Badge>
    </Tooltip>
  );
}

/**
 * RR triage page panel with two distinct sections:
 * - "Attached documents" — deliverable docs bound to this RR (detachable),
 *   each with its background-digest state (spinner / failed + retry).
 * - "Extracted from" — read-only provenance (which messages / files this RR
 *   was derived from).
 */
export function ReportRequestAttachments({
  reportRequestId,
}: Readonly<ReportRequestAttachmentsProps>) {
  const { attachments, sources, isLoading, detach, retryDigest, setClassification } =
    useReportRequestAttachments(reportRequestId);

  return (
    <Card shadow="sm" padding="md" radius="md">
      <Text fw={600} mb="xs">
        Attached documents
      </Text>
      {isLoading && attachments.length === 0 && (
        <Text size="sm" c="dimmed">
          Loading…
        </Text>
      )}
      {!isLoading && attachments.length === 0 && (
        <Text size="sm" c="dimmed">
          No documents attached.
        </Text>
      )}
      <Stack gap={6}>
        {attachments.map((a) => {
          // Adapt the RR-attachment row to the shared chip shape.
          const chip: ConversationAttachment = {
            id: a.conversationAttachmentId,
            chatThreadId: '',
            chatMessageId: null,
            parentAttachmentId: null,
            source: 'analyst_upload',
            classification: a.classification ?? 'internal',
            filename: a.filename ?? 'file',
            contentType: a.contentType,
            sizeBytes: a.sizeBytes ?? 0,
            sha256: null,
            freshdeskAttachmentId: null,
            createdByUserId: a.createdByUserId,
            createdAt: a.createdAt,
            downloadUrl: a.downloadUrl,
          };
          return (
            <MessageAttachment
              key={a.id}
              attachment={chip}
              compact
              action={
                <Group gap={4} wrap="nowrap">
                  <ClassificationBadge
                    attachment={a}
                    onFlip={() =>
                      void setClassification(
                        a.conversationAttachmentId,
                        a.classification === 'client' ? 'internal' : 'client'
                      )
                    }
                  />
                  <DigestChip
                    attachment={a}
                    onRetry={() => void retryDigest(a.conversationAttachmentId)}
                  />
                  <AttachmentRemoveButton
                    onRemove={() => void detach(a.conversationAttachmentId)}
                  />
                </Group>
              }
            />
          );
        })}
      </Stack>

      {sources.length > 0 && (
        <>
          <Text fw={600} mt="md" mb="xs">
            Extracted from
          </Text>
          <Stack gap={2}>
            {sources.map((s) => (
              <Text key={s.id} size="xs" c="dimmed">
                {s.filename ? `file: ${s.filename}` : 'message'}
                {s.sourceHint ? ` — ${s.sourceHint}` : ''}
              </Text>
            ))}
          </Stack>
        </>
      )}
    </Card>
  );
}
